/**
 * SmokingStoryPage.jsx
 * Magazine-style data storytelling page for QuitPact.
 * Team: TA37 - The Coders' Assembly
 */

import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import {
  fetchSmokingStoryInitialData,
  fetchMapData,
} from "../services/storyApi";
import "./SmokingStoryPage.css";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

function normalizeName(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[()'",.]/g, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getColor(value) {
  if (value == null || Number.isNaN(value)) return "#d1d5db";
  if (value < 10) return "#dcfce7";
  if (value < 20) return "#86efac";
  if (value < 30) return "#facc15";
  if (value < 40) return "#fb923c";
  return "#ef4444";
}

function formatValue(value, unit = "%") {
  if (value == null || Number.isNaN(Number(value))) return "No data";
  return `${Number(value).toFixed(1)}${unit || ""}`;
}

const CHAPTERS = [
  { id: "world", label: "World View", icon: "🌍" },
  { id: "australia", label: "Australia", icon: "🦘" },
  { id: "impact", label: "Health Impact", icon: "❤️" },
];

const IMPACT_ICONS = {
  "Deaths": "💀",
  "Burden of disease": "⚕️",
  "Linked conditions": "🫀",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="smoking-story-page__tooltip">
        <p className="smoking-story-page__tooltip-label">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="smoking-story-page__tooltip-value">
            {entry.name}: {Number(entry.value).toFixed(1)}%
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SmokingStoryPage = () => {
  const [trendData, setTrendData] = useState([]);
  const [ageData, setAgeData] = useState([]);
  const [impactData, setImpactData] = useState([]);
  const [mapData, setMapData] = useState([]);
  const [worldYears, setWorldYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeChapter, setActiveChapter] = useState("world");

  useEffect(() => {
    let ignore = false;
    async function loadPage() {
      try {
        setLoading(true);
        setError("");
        const { years, trend, ageBreakdown, impact } =
          await fetchSmokingStoryInitialData();
        if (ignore) return;
        const cleanedYears = (years || [])
          .map((year) => Number(year))
          .filter((year) => !Number.isNaN(year))
          .sort((a, b) => a - b);
        setWorldYears(cleanedYears);
        setTrendData(trend || []);
        setAgeData(ageBreakdown || []);
        setImpactData(impact || []);
        if (cleanedYears.length > 0) {
          setSelectedYear(cleanedYears[cleanedYears.length - 1]);
        }
      } catch (err) {
        if (!ignore) setError(err.message || "Failed to load page.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    loadPage();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    async function loadMap() {
      if (!selectedYear) return;
      try {
        setMapLoading(true);
        const rows = await fetchMapData(selectedYear);
        if (!ignore) setMapData(rows || []);
      } catch (err) {
        if (!ignore) setError(err.message || "Failed to load map data.");
      } finally {
        if (!ignore) setMapLoading(false);
      }
    }
    loadMap();
    return () => { ignore = true; };
  }, [selectedYear]);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["world", "australia", "impact"];
      for (const id of sections) {
        const el = document.getElementById(`section-${id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActiveChapter(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const worldDataMapByCode = useMemo(() => {
    const mapped = {};
    for (const row of mapData) {
      const code = String(row.country_code || "").trim().toUpperCase();
      if (!code) continue;
      mapped[code] = {
        country_name: row.country_name,
        smoking_percent: row.smoking_percent != null ? Number(row.smoking_percent) : null,
      };
    }
    return mapped;
  }, [mapData]);

  const worldDataMapByName = useMemo(() => {
    const mapped = {};
    for (const row of mapData) {
      const nameKey = normalizeName(row.country_name);
      if (!nameKey) continue;
      mapped[nameKey] = {
        country_name: row.country_name,
        smoking_percent: row.smoking_percent != null ? Number(row.smoking_percent) : null,
      };
    }
    return mapped;
  }, [mapData]);

  const sortedTrendData = useMemo(() => {
    return [...trendData].sort((a, b) => Number(a.year_value) - Number(b.year_value));
  }, [trendData]);

  const latestAustraliaValue = useMemo(() => {
    if (!sortedTrendData.length) return null;
    const latest = sortedTrendData[sortedTrendData.length - 1];
    return latest?.people_percent != null ? Number(latest.people_percent) : null;
  }, [sortedTrendData]);

  const earliestAustraliaValue = useMemo(() => {
    if (!sortedTrendData.length) return null;
    const first = sortedTrendData[0];
    return first?.people_percent != null ? Number(first.people_percent) : null;
  }, [sortedTrendData]);

  const declineValue = useMemo(() => {
    if (latestAustraliaValue == null || earliestAustraliaValue == null) return null;
    return Number((earliestAustraliaValue - latestAustraliaValue).toFixed(1));
  }, [latestAustraliaValue, earliestAustraliaValue]);

  const worldYearIndex = useMemo(() => {
    const idx = worldYears.findIndex((year) => Number(year) === Number(selectedYear));
    return idx >= 0 ? idx : 0;
  }, [worldYears, selectedYear]);

  const scrollToSection = (id) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="smoking-story-page__loading">
        <div className="smoking-story-page__loading-spinner" />
        <p>Loading the smoking story...</p>
      </div>
    );
  }

  if (error && !trendData.length && !ageData.length && !impactData.length) {
    return <div className="smoking-story-page__loading">{error}</div>;
  }

  return (
    <main className="smoking-story-page" aria-label="Smoking story">

      {/* -- Chapter nav -- */}
      <nav className="smoking-story-page__chapter-nav" aria-label="Page chapters">
        {CHAPTERS.map((chapter) => (
          <button
            key={chapter.id}
            className={`smoking-story-page__chapter-btn ${activeChapter === chapter.id ? "smoking-story-page__chapter-btn--active" : ""}`}
            onClick={() => scrollToSection(chapter.id)}
            type="button"
          >
            <span className="smoking-story-page__chapter-icon">{chapter.icon}</span>
            <span className="smoking-story-page__chapter-label">{chapter.label}</span>
          </button>
        ))}
      </nav>

      {/* -- Hero header -- */}
      <header className="smoking-story-page__header">
        <div className="smoking-story-page__badge" aria-hidden="true">
          🌍 Smokes & Stats
        </div>
        <h1 className="smoking-story-page__heading">
          From the world view to Australia's smoking story
        </h1>
        <p className="smoking-story-page__subheading">
          A data-driven look at global smoking prevalence, Australia's decline,
          and why the numbers still matter for your health today.
        </p>
      </header>

      {/* ===== CHAPTER 1: WORLD MAP ===== */}
      <section
        id="section-world"
        className="smoking-story-page__section"
        aria-label="World map"
      >
        <div className="smoking-story-page__section-header">
          <div className="smoking-story-page__chapter-tag">
            <span>🌍</span> Chapter 1
          </div>
          <p className="smoking-story-page__section-label">World Map</p>
          <h2 className="smoking-story-page__section-title">
            Smoking prevalence across countries
          </h2>
          <p className="smoking-story-page__section-copy">
            Select a year from the timeline below to see how global smoking rates have shifted over two decades. Darker colours indicate higher prevalence, countries shaded red still have more than 40% of adults smoking daily.
          </p>
        </div>

        <div className="smoking-story-page__panel smoking-story-page__panel--map">
          <div className="smoking-story-page__map-top">
            <h3 className="smoking-story-page__panel-title">
              World smoking prevalence
            </h3>
            <span className="smoking-story-page__map-year">
              {selectedYear || "No year selected"}
            </span>
          </div>

          {worldYears.length > 0 && (
            <div className="smoking-story-page__timeline-container">
              <div className="smoking-story-page__timeline-line" />
              <div
                className="smoking-story-page__timeline-grid"
                style={{ gridTemplateColumns: `repeat(${worldYears.length}, minmax(0, 1fr))` }}
              >
                {worldYears.map((year, index) => {
                  const isActive = Number(year) === Number(selectedYear);
                  const isPast = index <= worldYearIndex;
                  return (
                    <button
                      key={year}
                      type="button"
                      className={`smoking-story-page__timeline-step ${isActive ? "smoking-story-page__timeline-step--active" : ""} ${isPast ? "smoking-story-page__timeline-step--past" : ""}`}
                      onClick={() => setSelectedYear(year)}
                    >
                      <span className="smoking-story-page__timeline-dot" />
                      <span className="smoking-story-page__timeline-year">{year}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="smoking-story-page__legend">
            {[
              { cls: "0", label: "No data" },
              { cls: "1", label: "<10%" },
              { cls: "2", label: "10–19.9%" },
              { cls: "3", label: "20–29.9%" },
              { cls: "4", label: "30–39.9%" },
              { cls: "5", label: "40%+" },
            ].map(({ cls, label }) => (
              <div key={cls} className="smoking-story-page__legend-item">
                <span className={`smoking-story-page__legend-swatch smoking-story-page__legend-swatch--${cls}`} />
                {label}
              </div>
            ))}
          </div>

          <div className="smoking-story-page__map-box">
            {mapLoading ? (
              <div className="smoking-story-page__map-loading">Updating map...</div>
            ) : (
              <ComposableMap projection="geoNaturalEarth1">
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const geoCode = String(
                        geo.properties?.ISO_A3 || geo.properties?.iso_a3 || geo.properties?.id || ""
                      ).trim().toUpperCase();
                      const geoName = normalizeName(geo.properties?.name || geo.properties?.NAME || "");
                      const countryData = worldDataMapByCode[geoCode] || worldDataMapByName[geoName];
                      const value = countryData?.smoking_percent ?? null;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={getColor(value)}
                          stroke="#ffffff"
                          strokeWidth={0.6}
                          style={{
                            default: { outline: "none" },
                            hover: { outline: "none", opacity: 0.8 },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            )}
          </div>
        </div>

        {/* -- World insight callout -- */}
        <div className="smoking-story-page__insight-callout">
          <span className="smoking-story-page__insight-icon">💡</span>
          <div>
            <p className="smoking-story-page__insight-title">What you're looking at</p>
            <p className="smoking-story-page__insight-text">
              Each country is coloured by the percentage of adults who smoke daily. Grey means no data was available. Use the year selector to track how global smoking prevalence has changed, most high-income countries have seen significant declines, while some regions remain persistently high.
            </p>
          </div>
        </div>
      </section>

      {/* ===== CHAPTER 2: AUSTRALIA ===== */}
      <section
        id="section-australia"
        className="smoking-story-page__section"
        aria-label="Australia focus"
      >
        <div className="smoking-story-page__section-header">
          <div className="smoking-story-page__chapter-tag">
            <span>🦘</span> Chapter 2
          </div>
          <p className="smoking-story-page__section-label">Australia Focus</p>
          <h2 className="smoking-story-page__section-title">
            A closer look at Australia
          </h2>
          <p className="smoking-story-page__section-copy">
            Australia has been a world leader in tobacco control. Plain packaging, high excise taxes, and public health campaigns have driven one of the steepest smoking declines among developed nations. The numbers below tell that story.
          </p>
        </div>

        {/* -- Stat cards -- */}
        <div className="smoking-story-page__stats">
          <article className="smoking-story-page__stat-card">
            <div className="smoking-story-page__stat-icon">🚬</div>
            <p className="smoking-story-page__stat-label">Current daily smoking</p>
            <h3 className="smoking-story-page__stat-value">
              {latestAustraliaValue != null ? `${latestAustraliaValue.toFixed(1)}%` : "--"}
            </h3>
            <p className="smoking-story-page__stat-copy">
              Latest recorded daily smoking rate in Australia, down from over 22% in the 1990s.
            </p>
          </article>

          <article className="smoking-story-page__stat-card">
            <div className="smoking-story-page__stat-icon">📉</div>
            <p className="smoking-story-page__stat-label">Total decline</p>
            <h3 className="smoking-story-page__stat-value">
              {declineValue != null ? `${declineValue.toFixed(1)} pts` : "--"}
            </h3>
            <p className="smoking-story-page__stat-copy">
              Percentage point drop from the earliest recorded value. A direct result of sustained policy and public health effort.
            </p>
          </article>
        </div>

        {/* -- Trend chart -- */}
        <div className="smoking-story-page__panel">
          <div className="smoking-story-page__panel-top">
            <div>
              <h3 className="smoking-story-page__panel-title">
                Australia's daily smoking trend
              </h3>
              <p className="smoking-story-page__panel-subtitle">
                How the daily smoking rate has changed over time in Australia
              </p>
            </div>
            <span className="smoking-story-page__panel-tag">ABS series</span>
          </div>

          <div className="smoking-story-page__chart-narrative">
            <p>The line chart below tracks the percentage of Australians who smoked daily across multiple survey years. Notice the steeper decline after 2010, this coincides with Australia's plain packaging laws introduced in 2012, one of the first in the world.</p>
          </div>

          <div className="smoking-story-page__chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sortedTrendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="year_label" tick={{ fontSize: 12, fill: "#7c80a0" }} />
                <YAxis unit="%" tick={{ fontSize: 12, fill: "#7c80a0" }} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  x="2013"
                  stroke="#e05c5c"
                  strokeDasharray="4 4"
                  label={{ value: "Plain packaging", position: "top", fontSize: 11, fill: "#e05c5c" }}
                />
                <Line
                  type="monotone"
                  dataKey="people_percent"
                  stroke="#1d9e75"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "#1d9e75", strokeWidth: 2, stroke: "#fff" }}
                  activeDot={{ r: 7 }}
                  name="Daily smokers"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* -- Trend insight -- */}
        <div className="smoking-story-page__insight-callout">
          <span className="smoking-story-page__insight-icon">📖</span>
          <div>
            <p className="smoking-story-page__insight-title">What this means</p>
            <p className="smoking-story-page__insight-text">
              The decline in daily smoking is one of Australia's most significant public health achievements. Plain packaging, introduced in 2012, made Australia the first country in the world to mandate standardised tobacco packaging. Combined with regular tax increases, the result has been a steady, sustained fall in smoking rates across all age groups.
            </p>
          </div>
        </div>

        {/* -- Age breakdown chart -- */}
        <div className="smoking-story-page__panel">
          <div className="smoking-story-page__panel-top">
            <div>
              <h3 className="smoking-story-page__panel-title">
                Ex-smokers and never smoked by age
              </h3>
              <p className="smoking-story-page__panel-subtitle">
                How smoking history differs across age groups
              </p>
            </div>
            <span className="smoking-story-page__panel-tag">2022 breakdown</span>
          </div>

          <div className="smoking-story-page__chart-narrative">
            <p>This chart compares two groups across age brackets, those who have never smoked (green) and those who are ex-smokers (blue). Younger age groups show much higher "never smoked" rates, reflecting the generational shift in attitudes toward tobacco.</p>
          </div>

          <div className="smoking-story-page__chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="age_group" tick={{ fontSize: 11, fill: "#7c80a0" }} />
                <YAxis unit="%" tick={{ fontSize: 12, fill: "#7c80a0" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: "13px", paddingTop: "12px" }} />
                <Bar dataKey="ex_smoker_percent" fill="#378add" name="Ex-smoker" radius={[6, 6, 0, 0]} />
                <Bar dataKey="never_smoked_percent" fill="#1d9e75" name="Never smoked" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* -- Age insight -- */}
        <div className="smoking-story-page__insight-callout">
          <span className="smoking-story-page__insight-icon">💡</span>
          <div>
            <p className="smoking-story-page__insight-title">Key insight</p>
            <p className="smoking-story-page__insight-text">
              Younger Australians aged 15-24 are far more likely to have never smoked compared to older generations. This generational shift suggests that prevention campaigns and stricter regulations are having a lasting impact. Among older age groups, the higher proportion of ex-smokers reflects the large number of Australians who quit following public health campaigns of the 1980s and 1990s.
            </p>
          </div>
        </div>
      </section>

      {/* ===== CHAPTER 3: HEALTH IMPACT ===== */}
      <section
        id="section-impact"
        className="smoking-story-page__section"
        aria-label="Health impact"
      >
        <div className="smoking-story-page__section-header">
          <div className="smoking-story-page__chapter-tag">
            <span>❤️</span> Chapter 3
          </div>
          <p className="smoking-story-page__section-label">Health Impact</p>
          <h2 className="smoking-story-page__section-title">
            Why the trend still matters
          </h2>
          <p className="smoking-story-page__section-copy">
            Even as smoking rates decline, the health burden from tobacco remains enormous. These figures put the human cost of smoking into perspective — and show why every person who quits makes a real difference.
          </p>
        </div>

        <div className="smoking-story-page__impact-grid">
          {impactData.map((item, index) => (
            <article
              key={`${item.topic}-${item.metric_label}-${index}`}
              className="smoking-story-page__impact-card"
            >
              <div className="smoking-story-page__impact-icon">
                {IMPACT_ICONS[item.topic] || "📊"}
              </div>
              <p className="smoking-story-page__impact-topic">{item.topic}</p>
              <h3 className="smoking-story-page__impact-value">
                {item.value_text || formatValue(item.value_numeric, item.unit)}
              </h3>
              <p className="smoking-story-page__impact-metric">
                {item.metric_label}
                {item.year_value ? ` · ${item.year_value}` : ""}
              </p>
              <p className="smoking-story-page__impact-notes">{item.notes}</p>
            </article>
          ))}
        </div>

        {/* -- Final callout -- */}
        <div className="smoking-story-page__insight-callout smoking-story-page__insight-callout--green">
          <span className="smoking-story-page__insight-icon">🌱</span>
          <div>
            <p className="smoking-story-page__insight-title">The bigger picture</p>
            <p className="smoking-story-page__insight-text">
              Australia's decline in smoking is a public health success story but the job is not done. Around 1 in 9 Australian adults still smoke daily. Every person who quits reduces their own risk and contributes to lowering the national health burden. If you are trying to quit, understanding your personal relapse risk is one of the most powerful steps you can take.
            </p>
          </div>
        </div>
      </section>

      {/* -- CTA -- */}
      <section className="smoking-story-page__cta" aria-label="Next step">
        <p className="smoking-story-page__cta-eyebrow">Ready for the next step?</p>
        <h2 className="smoking-story-page__cta-heading">
          Understand your personal relapse risk
        </h2>
        <p className="smoking-story-page__cta-text">
          The data tells the national story. QuitPact helps you understand your own.
        </p>
        <a href="/check-in" className="smoking-story-page__cta-link">
          Take the check-in
        </a>
      </section>

    </main>
  );
};

export default SmokingStoryPage;